<template>
    <v-card elevation="3" class="half-card" ref="cardBody" style="overflow: auto">
        <v-btn-toggle v-model="currentView" mandatory density="compact" class="position-absolute view-toggle-group" style="right: 0;z-index: 1001;">
            <v-btn value="map" size="x-small" icon>
                <v-icon>mdi-map</v-icon>
            </v-btn>
            <v-btn value="table" size="x-small" icon>
                <v-icon>mdi-table</v-icon>
            </v-btn>
            <v-btn value="lot" size="x-small" icon :disabled="!hasLocations">
                <v-icon>mdi-parking</v-icon>
            </v-btn>
        </v-btn-toggle>
        <div v-if="currentView === 'map' || currentView === 'lot'" class="map-controls-btn-group">
            <v-btn v-if="currentView === 'map'"
                   @click="fitAllDevices"
                   size="x-small" icon
                   color="white"
                   elevation="2"
                   class="mb-1">
                <v-icon>mdi-fit-to-screen</v-icon>
            </v-btn>
            <v-btn v-if="currentView === 'map'"
                   @click="satellite = !satellite"
                   size="x-small" icon
                   color="white"
                   elevation="2"
                   class="mb-1">
                <v-icon>{{ satellite ? 'mdi-map-outline' : 'mdi-satellite-variant' }}</v-icon>
            </v-btn>
            <v-btn @click="toggleFullscreen"
                   size="x-small" icon
                   color="white"
                   elevation="2">
                <v-icon>{{ isFullscreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
            </v-btn>
        </div>
        <v-card-text class="pa-0">
            <div style="width:100%;" v-if="currentView === 'table'">
                <devices-data-table :devices="filteredDevices"
                                    :headers="headers"
                                    :selected-device="selectedDevice"
                />
            </div>
            <div v-else-if="currentView === 'lot'" style="position: relative;">
                <div class="fleet-filter-overlay" style="top: 55px;" @click.stop>
                    <v-btn v-if="!showFilters"
                           @click="openFilters"
                           size="x-small"
                           icon
                           color="white"
                           elevation="2">
                        <v-badge v-if="activeFilter" :content="activeFilterCount" color="primary">
                            <v-icon>mdi-filter-variant</v-icon>
                        </v-badge>
                        <v-icon v-else>mdi-filter-variant</v-icon>
                    </v-btn>
                    <div v-else class="filter-chips-container">
                        <v-btn @click="closeFilters" size="x-small" icon variant="text" class="me-1">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                        <v-chip v-for="chip in fleetChips" :key="chip.key"
                                :color="chip.count > 0 ? chip.color : undefined"
                                :variant="activeFilter === chip.key ? 'flat' : (chip.count > 0 ? 'tonal' : 'outlined')"
                                :prepend-icon="chip.icon"
                                density="comfortable"
                                size="small"
                                class="me-1 cursor-pointer"
                                @click="toggleFilter(chip.key)">
                            {{ chip.label }}: {{ chip.count }}
                        </v-chip>
                    </div>
                </div>
                <devices-lot-view :devices="filteredDevices"
                                  :company-locations="companyLocations"
                                  :height="mapHeight"
                                  :selected-device="selectedDevice"
                                  @selected-marker="(device)=>$emit('selectDevice',device)"
                />
            </div>
            <div v-else style="position: relative;" @click="onMapClick">
                <div class="fleet-filter-overlay" @click.stop>
                    <v-btn v-if="!showFilters"
                           @click="openFilters"
                           size="x-small"
                           icon
                           color="white"
                           elevation="2">
                        <v-badge v-if="activeFilter" :content="activeFilterCount" color="primary">
                            <v-icon>mdi-filter-variant</v-icon>
                        </v-badge>
                        <v-icon v-else>mdi-filter-variant</v-icon>
                    </v-btn>
                    <div v-else class="filter-chips-container">
                        <v-btn @click="closeFilters" size="x-small" icon variant="text" class="me-1">
                            <v-icon>mdi-close</v-icon>
                        </v-btn>
                        <v-chip v-for="chip in fleetChips" :key="chip.key"
                                :color="chip.count > 0 ? chip.color : undefined"
                                :variant="activeFilter === chip.key ? 'flat' : (chip.count > 0 ? 'tonal' : 'outlined')"
                                :prepend-icon="chip.icon"
                                density="comfortable"
                                size="small"
                                class="me-1 cursor-pointer"
                                @click="toggleFilter(chip.key)">
                            {{ chip.label }}: {{ chip.count }}
                        </v-chip>
                    </div>
                </div>
                <devices-map ref="devicesMapRef"
                             :devices="filteredDevices"
                             :all-devices="props.devices"
                             :height="mapHeight"
                             :zoomToDevice="zoomToDevice"
                             :selected-device="selectedDevice"
                             :nearestToDevice="nearestToDevice"
                             :companyLocations="companyLocations"
                             :server-fleet-counts="serverFleetCounts"
                             :satellite="satellite"
                             @selected-marker="(device)=>$emit('selectDevice',device)"
                             @openNotifications="()=>{$emit('openNotificationsTab')}"
                             @map-clicked="onMapClick"
                             @devices-stale="$emit('devicesStale')"
                />
            </div>
        </v-card-text>
    </v-card>
</template>

<script setup>
import {computed, onBeforeUnmount, onMounted, ref, watch} from "vue";
import DevicesMap from "@/components/cards/devices/DevicesMap.vue";
import DevicesDataTable from "@/components/cards/devices/DevicesDataTable.vue";
import DevicesLotView from "@/components/cards/devices/DevicesLotView.vue";
import {isMoving, isIdle, isStationary, isNonReporting, isLowBattery, filterDevicesByFleetStatus, isServerFilter} from "@/composables/fleetStatus";

const cardBody = ref();
const devicesMapRef = ref();
const mapHeight = ref(null);
const currentView = ref('map');
const showFilters = ref(false);
const isFullscreen = ref(false);
const satellite = ref(false);
let autoCloseTimer = null;
const emits = defineEmits(['selectDevice', 'openNotificationsTab', 'filterChanged', 'devicesStale']);
const props = defineProps(['selectedDevice', 'devices', 'headers', 'zoomToDevice', 'nearestToDevice', 'companyLocations', 'activeFleetFilter', 'serverFleetCounts']);
const activeFilter = computed(() => props.activeFleetFilter ?? null);

const hasLocations = computed(() => {
    return (props.companyLocations || []).some(loc =>
        loc.polygon_coordinates && loc.polygon_coordinates.length > 0
    );
});

const toggleFullscreen = () => {
    const el = cardBody.value?.$el;
    if (!el) return;
    if (!document.fullscreenElement) {
        el.requestFullscreen().catch(err => console.error('Fullscreen request failed:', err));
    } else {
        document.exitFullscreen();
    }
};

const onFullscreenChange = () => {
    isFullscreen.value = !!document.fullscreenElement;
    updateMapHeight();
};

const fitAllDevices = () => {
    devicesMapRef.value?.fitAllDevices();
};

const serverAssetIdsMap = computed(() => {
    const map = {};
    (props.serverFleetCounts || []).forEach(item => {
        if (item.asset_ids) {
            map[item.key] = new Set(item.asset_ids);
        }
    });
    return map;
});

const fleetChips = computed(() => {
    const devices = props.devices || [];
    const clientChips = [
        {key: 'total', label: 'Total', color: 'primary', icon: 'mdi-car-multiple', count: devices.length},
        {key: 'moving', label: 'Moving', color: 'green', icon: 'mdi-car-connected', count: devices.filter(d => isMoving(d) || isIdle(d)).length},
        {key: 'idle', label: 'Idling', color: 'amber', icon: 'mdi-car-clock', count: devices.filter(isIdle).length},
        {key: 'stationary', label: 'Stationary', color: 'grey', icon: 'mdi-car-brake-parking', count: devices.filter(isStationary).length},
        {key: 'non_reporting', label: 'Offline', color: 'red', icon: 'mdi-signal-off', count: devices.filter(isNonReporting).length},
        {key: 'low_battery', label: 'Low Battery', color: 'orange', icon: 'mdi-battery-low', count: devices.filter(isLowBattery).length},
    ];

    const serverChips = (props.serverFleetCounts || [])
        .filter(item => isServerFilter(item.key))
        .map(item => ({
            key: item.key,
            label: item.label,
            color: item.color,
            icon: item.icon,
            count: item.count,
        }));

    return [...clientChips, ...serverChips];
});

const activeFilterCount = computed(() => {
    if (!activeFilter.value) return 0;
    const chip = fleetChips.value.find(c => c.key === activeFilter.value);
    return chip ? chip.count : 0;
});

const filteredDevices = computed(() => filterDevicesByFleetStatus(props.devices, activeFilter.value, serverAssetIdsMap.value));

const startAutoCloseTimer = () => {
    clearAutoCloseTimer();
    autoCloseTimer = setTimeout(() => {
        showFilters.value = false;
    }, 10000);
};

const clearAutoCloseTimer = () => {
    if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
        autoCloseTimer = null;
    }
};

const openFilters = () => {
    showFilters.value = true;
    startAutoCloseTimer();
};

const closeFilters = () => {
    showFilters.value = false;
    clearAutoCloseTimer();
};

const onMapClick = () => {
    if (showFilters.value) {
        closeFilters();
    }
};

const toggleFilter = (key) => {
    emits('filterChanged', activeFilter.value === key ? null : key);
    closeFilters();
};

const updateMapHeight = () => {
    if (isFullscreen.value) {
        mapHeight.value = '100vh';
    } else {
        mapHeight.value = ((cardBody.value && cardBody.value.$el && cardBody.value.$el.offsetHeight > 0) ? cardBody.value.$el.offsetHeight : 260) + 'px';
    }
};

onMounted(() => {
    updateMapHeight()
    window.addEventListener('resize', updateMapHeight)
    document.addEventListener('fullscreenchange', onFullscreenChange)
})

onBeforeUnmount(() => {
    window.removeEventListener('resize', updateMapHeight)
    document.removeEventListener('fullscreenchange', onFullscreenChange)
    clearAutoCloseTimer();
})
</script>

<style scoped>
.fleet-filter-overlay {
    position: absolute;
    top: 80px;
    left: 8px;
    z-index: 1001;
    display: flex;
    align-items: center;
}

.filter-chips-container {
    display: flex;
    align-items: center;
    background: rgba(255, 255, 255, 0.95);
    border-radius: 8px;
    padding: 4px 8px;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
    flex-wrap: wrap;
    max-width: calc(100% - 16px);
    gap: 2px;
}

.map-controls-btn-group {
    position: absolute;
    bottom: 8px;
    right: 8px;
    z-index: 1001;
    display: flex;
    flex-direction: column;
    align-items: center;
}

.view-toggle-group {
    border-radius: 4px;
    overflow: hidden;
}
</style>
